import {
  Box,
  Button,
  Chip,
  List,
  ListItem,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import {
  BooleanExplain,
  BooleanThreePart,
  BooleanTwoPart,
  Company,
  CompanyAddressType,
  CompanyNameShowToFN,
  CountryCode,
  Employer,
  ForeignNational,
  I140,
  J1J2,
  Money,
  PersonType,
  ProcessRequestLocationType,
  ShippingPaymentPreference,
  ShippingPreference,
  StreetAddress,
  TeamMember,
} from 'API';
import { Picture } from 'models';
import { Link } from 'react-router-dom';
import CurrencyDisplay from 'components/Currency';
import InlineFileModalButton from 'components/InlineFileModalButton';
import S3Image from 'components/S3Image';
import StreetAddressDisplay from 'components/StreetAddressDisplay';
import S3Avatar from 'components/S3Avatar';
import { intComma } from 'humanize-plus';
import { format as formatDate, parseISO } from 'date-fns';
import { ReactNode, useContext } from 'react';
import CompanyService from 'services/company';
import PersonService from 'services/person';
import PictureService from 'services/picture';
import ProcessService from 'services/process';
import { DICTIONARY, JOB_URL } from 'shared/constants';
import CompanyContext from 'shared/contexts/CompanyContext';
import ProfileContext from 'shared/contexts/ProfileContext';
import { resolvers } from 'shared/enums';
import { countryCodeResolver } from 'shared/geoResolvers';
import { notNullFilter, countriesAsList, getDisplayCompanyName } from 'shared/utilities';
import ProcessContext from 'shared/contexts/ProcessContext';
import JobDescriptionContext from 'shared/contexts/JobDescriptionContext';
import ToggleableValueDisplay from './ToggleableValueDisplay';

interface ValueProps {
  field: ToggleableField;
  parentClasses: Record<string, string>;
  renderAsList?: boolean;
  value: any;
}

interface EmptyProps {
  text?: string;
}

interface WrapperProps {
  children: ReactNode;
  renderAsList?: boolean;
}

const EmptyValue: React.FC<EmptyProps> = ({ text }: EmptyProps) => (
  <Typography sx={(t) => ({ color: t.palette.text.disabled })}>
    {text || '-'}
  </Typography>
);

const ValueWrapper: React.FC<WrapperProps> = ({
  children,
  renderAsList,
}: WrapperProps) => renderAsList ? (
  <Box sx={{ display: 'flex', marginTop: '0.5rem' }}>
    <Box flexGrow={0} sx={{ pr: 1 }}>
      •
    </Box>
    <Box flexGrow={1}>
      {children}
    </Box>
  </Box>
) : (
  <Typography sx={{ margin: '0.125rem 0 0.575rem' }} component="div">
    {children}
  </Typography>
);

const ToggleableFieldValue: React.FC<ValueProps> = ({
  field,
  parentClasses,
  renderAsList,
  value,
}: ValueProps) => {
  const [company] = useContext(CompanyContext);
  const [process] = useContext(ProcessContext);
  const [profile, , profileType] = useContext(ProfileContext);
  const [jobDescription] = useContext(JobDescriptionContext);
  const source = company || process || profile || jobDescription;
  let address: StreetAddress;

  switch (field.type) {
    case 'company-address': {
      address = value as StreetAddress;

      return address
        && (address.address || address.city || address.adminArea) ? (
          <>
            <StreetAddressDisplay
              address={address}
              hideCountry={address.country === CountryCode.US}
              variant="body2"
            />
            {address.companyAddressType
            && address.companyAddressType
            === (company as Company)?.data?.preferredMailingAddress && (
              <Typography variant="body2">
                {`- ${DICTIONARY.PreferredMailingAddress}`}
              </Typography>
            )}
          </>
        ) : (
          <EmptyValue />
        );
    }

    case 'address': {
      address = value as StreetAddress;

      return address && address.address ? (
        <>
          <StreetAddressDisplay
            address={address}
            hideCountry={field.config?.isUS}
            variant="body2"
          />
        </>
      ) : (
        <EmptyValue />
      );
    }

    case 'radio': {
      return value == null ? <EmptyValue /> : (

        <ValueWrapper>
          <Typography variant="body2">
            {field.fieldName === 'preferredMailingAddress' && (
              <>
                {value === CompanyAddressType.HEADQUARTERS ? 'Main Headquarters' : 'Additional Company Address'}
              </>
            )}
            {field.fieldName === 'companyNameShowToFN' && (
              <>
                {value === CompanyNameShowToFN.DOING_BUSINESS_AS ? '"Doing Business As" Name of Company' : 'Legal Name of U.S. Company'}
              </>
            )}
          </Typography>
        </ValueWrapper>
      );
    }

    case 'avatar': {
      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Chip
            avatar={(
              <S3Avatar
                picture={value.avatar}
                alt={PersonService.fullname(value)}
              />
            )}
            label={PersonService.fullname(value)}
          />
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );
    }

    case 'boolean':
      if (field.fieldName === 'premiumProcessing') {
        return value == null ? <Typography variant="body2">Not Applicable</Typography> : (
          <ValueWrapper renderAsList={renderAsList}>
            <Typography variant="body2">
              {value ? 'Yes' : 'No'}
            </Typography>
          </ValueWrapper>
        );
      }

      return value == null ? <EmptyValue /> : (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            {value ? 'Yes' : 'No'}
          </Typography>
        </ValueWrapper>
      );

    case 'boolean-three-part': {
      let display = '';
      if ((value as BooleanThreePart)?.value) {
        display = 'Yes';
      } else if ((value as BooleanThreePart)?.value === false) {
        display = 'No';
      }

      return display ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2">
                {display}
              </Typography>
            </Box>
            {display === 'Yes' && (
              <>
                <Typography sx={{ fontWeight: 700, color: '#5b5b63' }}>{field.explainPrompt}</Typography>
                <ValueWrapper renderAsList={renderAsList}>
                  <Typography variant="body2" id={`${field.fieldName}-followup`}>
                    {value.followValue ? 'Yes' : 'No'}
                  </Typography>
                </ValueWrapper>
                {value.followValueSecond !== null && (
                  <>
                    <Typography sx={{ fontWeight: 700, color: '#5b5b63' }}>{field.explainPromptSecond}</Typography>
                    <ValueWrapper renderAsList={renderAsList}>
                      <Typography variant="body2" id={`${field.fieldName}-followupSecond`}>
                        {value.followValueSecond ? 'Yes' : 'No'}
                        {' '}

                      </Typography>
                    </ValueWrapper>
                  </>
                )}

              </>
            )}
          </Box>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );
    }
    case 'company':
      if (profileType === PersonType.Employer) {
        return (
          <ValueWrapper>
            <List dense>
              {((profile as unknown) as Employer)?.companies?.items
                ?.filter(notNullFilter)
                .map((item) => item?.company as Company)
                .filter(notNullFilter)
                .map((comp: Company) => (
                  <ListItem key={comp!.id} style={{ padding: 0 }}>
                    <Link to={CompanyService.link(comp as Company)}>
                      {comp!.name}
                    </Link>
                  </ListItem>
                ))}
            </List>
          </ValueWrapper>
        );
      }

      if (profileType === PersonType.ForeignNational) {
        const comp = ((profile as unknown) as ForeignNational).company;

        return comp ? (
          <ValueWrapper>
            <Typography variant="body2">
              <Link to={CompanyService.link(comp as Company)}>
                {getDisplayCompanyName(comp?.displayCompanyName, comp?.name)}
              </Link>
            </Typography>
          </ValueWrapper>
        ) : (
          <EmptyValue />
        );
      }

      return <EmptyValue />;

    case 'criminal-history':
      return (
        <ValueWrapper>
          {ProcessService.crimeFields.map((crime) => (
            <Box
              key={crime}
              display="flex"
              sx={{
                padding: '0.125rem 0',
                '&:not(:last-child)': { borderBottom: (t) => `1px solid ${t.palette.divider}` },
                '&:first-of-type': { paddingTop: 0 },
                '&:last-child': { paddingBottom: 0 },
              }}
            >
              <Box sx={{ width: 40 }} flexGrow={0} flexShrink={0}>
                <Typography variant="body2">
                  {!value || (value as any)[crime] == null
                    ? '-'
                    : (value as any)[crime]
                      ? 'Yes'
                      : 'No'}
                </Typography>
              </Box>
              <Box flexGrow={1}>
                <Typography variant="body2">
                  {(DICTIONARY.CriminalHistory as any)[crime]}
                </Typography>
              </Box>
            </Box>
          ))}
        </ValueWrapper>
      );

    case 'date':
      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            {formatDate(parseISO(value as string), 'M/d/yy')}
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'enum':
    case 'enum-radio':
      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            {field.enumResolver(value as string)}
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'boolean-explain': {
      let display = '';
      if ((value as BooleanExplain)?.value) {
        display = 'Yes';
      } else if ((value as BooleanExplain)?.value === false) {
        display = 'No';
      }

      return display ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            {display}
            {(value as BooleanExplain).value
              && (value as BooleanExplain).reason
              && `. ${(value as BooleanExplain).reason}`}
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );
    }

    case 'boolean-two-part': {
      let display = '';
      if ((value as BooleanTwoPart)?.value) {
        display = 'Yes';
      } else if ((value as BooleanTwoPart)?.value === false) {
        display = 'No';
      }

      let followDisplay = '';
      if ((value as BooleanTwoPart)?.followValue) {
        followDisplay = 'Yes';
      } else if ((value as BooleanTwoPart)?.followValue === false) {
        followDisplay = 'No';
      } else {
        followDisplay = 'Unknown';
      }

      return display ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2">
                {display}
              </Typography>
            </Box>
            {display === 'Yes' && (
              <>
                <Typography>{field.explainPrompt}</Typography>
                <Typography variant="body2" id={`${field.fieldName}-followup`}>
                  {followDisplay}
                </Typography>
              </>
            )}
          </Box>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );
    }

    case 'checkbox':
      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            {value}
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'company-logo':
      return (value as Picture)?.original?.key ? (
        <ValueWrapper>
          <S3Image
            alt={`${company?.name} logo`}
            style={{ margin: '0.5rem 0 0', maxHeight: '6rem' }}
            imgkey={PictureService.targetPictureSize(value as Picture, 200).key}
          />
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'country':
      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            {countryCodeResolver(value as CountryCode)}
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'countries': {
      return value && (value as Array<CountryCode>).length > 0 ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            {countriesAsList(value)}
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );
    }

    case 'file': {
      let file = (source as any)[field.connectionField!];
      if (field.fieldName === 'uploadedDescriptionID' && jobDescription?.uploadedDescription) {
        file = (jobDescription as any)[field.connectionField!];
      }

      return value && file ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            <InlineFileModalButton
              file={file}
            >
              <Box component="span" sx={(t) => ({ color: t.palette.primary.main, '&:hover': { textDecoration: 'underline' } })}>
                {file.fileName}
              </Box>
            </InlineFileModalButton>
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );
    }

    case 'form-instructions':
      return (
        <Box sx={(t) => ({ borderBottom: `1px solid ${t.palette.divider}`, marginBottom: '1rem', paddingBottom: '2rem' })}>
          <Typography sx={(t) => ({ fontSize: '1rem', fontWeight: t.typography.fontWeightLight, lineHeight: 1.5, maxWidth: '28rem' })}>
            {field.text}
          </Typography>
        </Box>
      );

    case 'integer':

      // eslint-disable-next-line no-constant-condition, no-restricted-globals
      return typeof (value === 'number' || 'string') && !isNaN(value) && value !== null ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">{intComma(value)}</Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'i140':
      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2">
              {(value as I140).hasApproved ? 'Yes' : 'No'}
            </Typography>
          </Box>
          {(value as I140).hasApproved && (
            <>
              <Box sx={{ mb: 1 }}>
                <Typography className={parentClasses.label}>
                  {DICTIONARY.I140.classificationPrompt}
                </Typography>
                <Typography variant="body2">
                  {(value as I140).employmentBasedCategory == null
                    ? '-'
                    : resolvers.EmploymentBasedCategory(
                      (value as I140).employmentBasedCategory!,
                    )}
                </Typography>
              </Box>
              <Typography className={parentClasses.label}>
                {DICTIONARY.I140.priorityDatePrompt}
              </Typography>
              <Typography variant="body2">
                {(value as I140).priorityDate
                  ? formatDate(parseISO((value as I140).priorityDate!), 'M/d/yy')
                  : '-'}
              </Typography>
              <Typography className={parentClasses.label} sx={{ marginTop: '8px' }}>
                {DICTIONARY.I140.petitionerI140}
              </Typography>
              <Typography variant="body2">
                {(value as I140).petitionerI140}
              </Typography>
            </>
          )}
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'j1j2':
      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            {(value as J1J2).hasHad ? 'Yes' : 'No'}
          </Typography>
          {(value as J1J2).start && (
            <Typography variant="body2">
              {formatDate(parseISO((value as J1J2).start!), 'M/d/yy')}
              {' '}
              -
              {' '}
              {(value as J1J2).end
                && formatDate(parseISO((value as J1J2).end!), 'M/d/yy')}
            </Typography>
          )}
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'job-description':
      return ((profile as unknown) as ForeignNational)?.jobDescription
        ?.title ? (
          <ValueWrapper renderAsList={renderAsList}>
            <Typography variant="body2">
              {((profile as unknown) as ForeignNational)?.jobDescription?.title}
            </Typography>
          </ValueWrapper>
        ) : (
          <EmptyValue />
        );

    case 'manager': {
      const { manager } = (profile as unknown) as TeamMember;

      return manager ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            <Link to={PersonService.link(manager)}>
              {PersonService.fullname(manager)}
            </Link>
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );
    }

    case 'money':
    case 'salary':
      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            <CurrencyDisplay value={value as Money} />
            {field.type === 'salary' && ` ${(value as Money).period
              ? resolvers.CurrencyPeriod((value as Money).period!)
              : 'per year'}`}
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'prevailing-wage-case-number':
      return (
        <ValueWrapper>
          <Box display="flex" alignItems="center">
            <Box sx={{ mr: 'auto' }}>
              <Typography>
                {value || 'No value'}
              </Typography>
            </Box>
            {value && (
              <Button
                className="button--blue"
                variant="contained"
                color="secondary"
                component="a"
                target="_blank"
                href="https://flag.dol.gov/case-status-search"
              >
                Check
              </Button>
            )}
          </Box>
        </ValueWrapper>
      );

    case 'process-dependents':
      if (process?.foreignNational) {
        return process?.foreignNational?.dependents != null ? (
          <ValueWrapper renderAsList={renderAsList}>
            <Typography variant="body2">{process.foreignNational.dependents}</Typography>
          </ValueWrapper>
        ) : (
          <EmptyValue />
        );
      }

      return value != null ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">{value}</Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'process-email':
      if (process?.foreignNational) {
        return process?.foreignNational?.email ? (
          <ValueWrapper renderAsList={renderAsList}>
            <Typography variant="body2">{process.foreignNational.email}</Typography>
          </ValueWrapper>
        ) : (
          <EmptyValue />
        );
      }

      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">{value}</Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'process-firstname':
      if (process?.foreignNational) {
        return process?.foreignNational?.firstname ? (
          <ValueWrapper renderAsList={renderAsList}>
            <Typography variant="body2">{process.foreignNational.firstname}</Typography>
          </ValueWrapper>
        ) : (
          <EmptyValue />
        );
      }

      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">{value}</Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'process-lastname':
      if (process?.foreignNational) {
        return process?.foreignNational?.lastname ? (
          <ValueWrapper renderAsList={renderAsList}>
            <Typography variant="body2">{process.foreignNational.lastname}</Typography>
          </ValueWrapper>
        ) : (
          <EmptyValue />
        );
      }

      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">{value}</Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'process-spouse':
      if (process?.foreignNational) {
        return process?.foreignNational?.spouse != null ? (
          <ValueWrapper renderAsList={renderAsList}>
            <Typography variant="body2">{process.foreignNational.spouse ? 'Yes' : 'No'}</Typography>
          </ValueWrapper>
        ) : (
          <EmptyValue />
        );
      }

      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">{value ? 'Yes' : 'No'}</Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'process-location':
      return value?.type ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            {field.enumResolver(value.type as string)}
          </Typography>
          {value?.type === ProcessRequestLocationType.HEADQUARTERS
            && process?.company?.data?.addressPrimary && (
            <StreetAddressDisplay
              address={process?.company?.data?.addressPrimary}
              variant="body2"
            />
          )}
          {value?.type === ProcessRequestLocationType.SECONDARY
            && process?.company?.data?.addressSecondary && (
            <StreetAddressDisplay
              address={process?.company?.data?.addressSecondary}
              variant="body2"
            />
          )}
          {value?.type === ProcessRequestLocationType.REMOTE
            && process?.employmentData?.location?.remoteAddress && (
            <StreetAddressDisplay
              address={process?.employmentData?.location?.remoteAddress}
              variant="body2"
            />
          )}

        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'shipping':
      return (value as ShippingPreference)?.preference ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            {resolvers.ShippingPaymentPreference(
              (value as ShippingPreference)
                .preference as ShippingPaymentPreference,
            )}
            {(value as ShippingPreference).preference
              === ShippingPaymentPreference.FEDEX
              && (value as ShippingPreference).fedexAccount
              && `, #${(value as ShippingPreference).fedexAccount}`}
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'email':
    case 'phone':
    case 'string':

      if (field.fieldName === JOB_URL) {
        return value ? (
          <ValueWrapper renderAsList={renderAsList}>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line', wordBreak: 'break-all' }}
            >
              <MuiLink href={value} target="_blank">{value}</MuiLink>
            </Typography>
          </ValueWrapper>
        ) : (
          <EmptyValue />
        );
      }

      if (field.fieldName === 'usSSN') {
        return value ? (
          <ValueWrapper renderAsList={renderAsList}>
            <Box sx={{ whiteSpace: 'pre-line', wordBreak: 'break-all' }}>
              <ToggleableValueDisplay value={value} />
            </Box>
          </ValueWrapper>
        ) : (
          <EmptyValue />
        );
      }

      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-line', wordBreak: 'break-all' }}
          >
            {value}
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'select':
      return value ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2">
            {field.enumResolver
              ? field.enumResolver(value)
              : value}
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'multiselect':
    case 'stringArray':
      return value && (value as Array<string>).length > 0 ? (
        <ValueWrapper renderAsList={renderAsList}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-all' }}>
            {(value as Array<string>).join(', ')}
          </Typography>
        </ValueWrapper>
      ) : (
        <EmptyValue />
      );

    case 'subhead':
      return (
        <Box sx={{ marginBottom: '-0.5rem', '&:not(:first-of-type)': { marginTop: '1rem' } }}>
          {field.text && (
            <Typography
              component="h2"
              sx={(t) => ({ fontWeight: t.typography.fontWeightBold, textTransform: 'uppercase' })}
            >
              {field.text}
            </Typography>
          )}
          {field.subtext && (
            <Typography>
              {field.subtext}
            </Typography>
          )}
        </Box>
      );

    default:
      return <ValueWrapper>Other</ValueWrapper>;
  }
};

export default ToggleableFieldValue;
